import { TrendingUp, TrendingDown, DollarSign, Briefcase } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { computeIndexFundMetrics } from '@indexFundUtils';
import {IndexFundHolding} from "@/integrations/supabase/indexfunds.models.ts";

interface IndexFundMetricsCardsProps {
  holdings: IndexFundHolding[];
  prices: Record<string, IndexFundPrice>;
  isLoading: boolean;
  currency: string;
  convertAmount: (amount: number, from: string) => number;
}

export interface PortfolioMetrics {
  totalValue: number;
  totalCostBasis: number;
  totalReturn: number;
  totalReturnPct: number;
  holdingsCount: number;
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function computeIndexFundPortfolioMetrics(
  holdings: IndexFundHolding[],
  prices: Record<string, IndexFundPrice>,
  currency: string,
  convertAmount: (amount: number, from: string) => number
): PortfolioMetrics {
  let totalValue = 0;
  let totalCostBasis = 0;
  let totalReturn = 0;

  for (const holding of holdings) {
    const priceData = prices[holding.fund_symbol];
    const currentNav = priceData?.price ?? 0;
    const priceCurrency = priceData?.currency ?? holding.currency;
    const metrics = computeIndexFundMetrics(holding, currentNav);

    totalValue += convertAmount(metrics.marketValue, priceCurrency);
    totalCostBasis += convertAmount(metrics.costBasis, holding.currency);
    totalReturn += convertAmount(metrics.totalReturn, priceCurrency);
  }

  const totalReturnPct = totalCostBasis > 0 ? (totalReturn / totalCostBasis) * 100 : 0;

  return {
    totalValue,
    totalCostBasis,
    totalReturn,
    totalReturnPct,
    holdingsCount: holdings.length,
  };
}

function MetricCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-28" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-32" />
      </CardContent>
    </Card>
  );
}

export function IndexFundMetricsCards({
  holdings,
  prices,
  isLoading,
  currency,
  convertAmount,
}: IndexFundMetricsCardsProps) {
  if (isLoading) {
    return (
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        role="status"
        aria-label="Loading metrics"
        aria-busy="true"
        aria-live="polite"
      >
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
        <MetricCardSkeleton />
      </div>
    );
  }

  const { totalValue, totalCostBasis, totalReturn, totalReturnPct, holdingsCount } =
    computeIndexFundPortfolioMetrics(holdings, prices, currency, convertAmount);

  const isPositiveReturn = totalReturn >= 0;

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      role="region"
      aria-label="Index fund portfolio metrics"
    >
      {/* Total Value */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold" data-testid="total-value">
            {formatCurrency(totalValue, currency)}
          </div>
        </CardContent>
      </Card>

      {/* Total Cost Basis */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Total Cost Basis
          </CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold" data-testid="total-cost-basis">
            {formatCurrency(totalCostBasis, currency)}
          </div>
        </CardContent>
      </Card>

      {/* Total Return */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Return</CardTitle>
          {isPositiveReturn ? (
            <TrendingUp className="h-4 w-4 text-emerald-600" aria-hidden="true" />
          ) : (
            <TrendingDown className="h-4 w-4 text-red-600" aria-hidden="true" />
          )}
        </CardHeader>
        <CardContent>
          <div
            className={`text-2xl font-bold ${isPositiveReturn ? 'text-emerald-600' : 'text-red-600'}`}
            data-testid="total-return"
          >
            {formatCurrency(totalReturn, currency)}
            <span className="text-sm font-normal ml-1">({totalReturnPct.toFixed(2)}%)</span>
          </div>
        </CardContent>
      </Card>

      {/* Number of Holdings */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Holdings</CardTitle>
          <Briefcase className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold" data-testid="holdings-count">
            {holdingsCount}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
